import cn from 'classnames'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import UserHandleFromId from 'components/shared/UserHandleFromId'
import React, { useEffect, useRef, useState } from 'react'
import { isMobile } from 'lib/device'
import { Banana, Wallet, Hash, User, FileType, Fingerprint, Copy, Check } from 'lucide-react'
import styles from '../answer.module.scss'
import { OrdinalInfoPopupPortal } from './OrdinalInfoPopupPortal'
import { Ordinal } from './types'

interface OrdinalInfoPopupProps {
    /** The ordinal to display information about */
    ordinal: Ordinal
    /** Function to call when the popup is closed */
    onClose: () => void
    /** Function to open the ordinals modal */
    openOrdinalsModal: (origin?: string) => void
    /** Reference to the button that triggered the popup */
    buttonRef: React.RefObject<HTMLDivElement>
}

/**
 * Popup component that displays information about an ordinal.
 * Uses a portal to ensure it's not clipped by container overflow.
 */
export const OrdinalInfoPopup: React.FC<OrdinalInfoPopupProps> = ({
    ordinal,
    onClose,
    openOrdinalsModal,
    buttonRef,
}) => {
    const [ordinalDetails, setOrdinalDetails] = useState<Ordinal | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({
        top: 0,
        left: 0,
    })
    const [copySuccess, setCopySuccess] = useState<boolean>(false)
    const popupRef = useRef<HTMLDivElement>(null)
    const inscriptionOutpoint = ordinal?.origin?.split(':')[0] || ''

    // Calculate position based on button ref and ensure it's visible on screen
    const calculateSafePosition = (): { top: number; left: number } => {
        if (!buttonRef?.current) return { top: 0, left: 0 }

        const buttonRect = buttonRef.current.getBoundingClientRect()
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        const popupWidth = 280 // Match width from CSS
        const popupHeight = 300 // Approximate height

        // Initial position - above the button
        let top = buttonRect.top - 10
        let left = buttonRect.left - 240 + buttonRect.width

        // Check if popup would be off-screen to the right
        if (left + popupWidth > windowWidth) {
            left = windowWidth - popupWidth - 10 // 10px margin from right edge
        }

        // Check if popup would be off-screen to the left
        if (left < 10) {
            left = 10 // 10px margin from left edge
        }

        // Check if popup would be off-screen at the top
        if (top < 10) {
            // Position below the button instead
            top = buttonRect.bottom + 10
        }

        // Check if popup would be off-screen at the bottom
        if (top + popupHeight > windowHeight) {
            // If positioning below would be off-screen,
            // and there's more space above than below, position above
            if (buttonRect.top > windowHeight - buttonRect.bottom) {
                top = Math.max(10, buttonRect.top - popupHeight - 10)
            } else {
                // Otherwise, position as high as possible while keeping on screen
                top = windowHeight - popupHeight - 10
            }
        }

        return { top, left }
    }

    // Update position whenever window is resized
    useEffect(() => {
        const updatePosition = (): void => {
            setPopupPosition(calculateSafePosition())
        }

        // Set initial position
        updatePosition()

        // Update on resize
        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('resize', updatePosition)
        }
    }, [buttonRef])

    useEffect(() => {
        const fetchOrdinalDetails = async (): Promise<void> => {
            setIsLoading(true)
            try {
                setOrdinalDetails(ordinal)
            } catch (error) {
                console.error('Error fetching ordinal details:', error)
            } finally {
                setIsLoading(false)
            }
        }

        fetchOrdinalDetails()

        // Add event listener to close popup when clicking outside
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node) &&
                buttonRef?.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [ordinal, onClose, buttonRef])

    const isOwner = ordinal?.treechat_owner_id === String(window.gon?.currentUser?.id)

    // Format the origin to display in a readable way
    const formatOrigin = (origin?: string): string => {
        if (!origin) return 'Unknown'
        // Keep the full string including underscore and vout, just truncate the middle
        if (origin.length > 15) {
            return `${origin.substring(0, 6)}...${origin.substring(origin.length - 6)}`
        }
        return origin
    }

    // Render owner information
    const renderOwner = (): React.ReactNode => {
        if (isOwner) return 'You'

        // If there's a treechat owner ID, use the UserHandleFromId component
        if (ordinal?.treechat_owner_id) {
            return <UserHandleFromId userId={ordinal.treechat_owner_id} afterClick={onClose} />
        }

        // Fall back to the owner_name if provided
        if (ordinal?.owner_name) {
            return ordinal.owner_name
        }

        return (
            <span
                className={styles.ordinalOwnerTreechatIcon}
                aria-label="No Treechat owner linked"
                title="No Treechat owner linked"
            >
                <TreechatIcon size={18} />
            </span>
        )
    }

    // Handle copying origin to clipboard
    const handleCopyOrigin = (): void => {
        if (!ordinal?.origin) return

        navigator.clipboard
            .writeText(ordinal.origin)
            .then(() => {
                setCopySuccess(true)
                // Reset copy success after 1.5 seconds
                setTimeout(() => setCopySuccess(false), 1500)
            })
            .catch(err => {
                console.error('Failed to copy text: ', err)
            })
    }

    const openExternal = (url: string): void => {
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const explorerLinks = inscriptionOutpoint
        ? [
              {
                  key: '1sat',
                  label: '1Sat',
                  title: '1Sat Ordinals',
                  className: styles.oneSatButton,
                  icon: <OrdinalsIcon size={16} />,
                  url: `https://alpha.1satordinals.com/outpoint/${inscriptionOutpoint}/timeline`,
              },
              {
                  key: 'zoide',
                  label: 'Zoide',
                  title: 'Zoide',
                  className: styles.zoideButton,
                  icon: <img src="/zoide-dark.png" alt="Zoide" />,
                  url: `https://zoide.io/market/inscription/${inscriptionOutpoint}`,
              },
              {
                  key: '3dordi',
                  label: '3DOrdi',
                  title: '3DOrdi',
                  className: styles.threeDOrdiButton,
                  icon: <img src="/assets/images/ordi-mascot.png" alt="3DOrdi" />,
                  url: `https://3dordi.io/ordinal/${inscriptionOutpoint}`,
              },
              {
                  key: 'gorillapool',
                  label: 'Gorilla Pool',
                  title: 'ordinals.gorillapool.io',
                  className: styles.gorillaPoolButton,
                  icon: <Banana size={16} />,
                  url: `https://ordinals.gorillapool.io/content/${inscriptionOutpoint}`,
              },
              {
                  key: 'ordfs',
                  label: 'ORDFS',
                  title: 'ordfs.network',
                  className: styles.ordfsButton,
                  icon: <span className={styles.explorerTextIcon}>ORDFS</span>,
                  url: `https://ordfs.network/content/${inscriptionOutpoint}`,
              },
          ]
        : []

    return (
        <OrdinalInfoPopupPortal>
            <div
                className={cn(styles.ordinalInfoPopup, {
                    [styles.mobileOrdinalInfoPopup]: isMobile,
                })}
                onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                style={{
                    position: 'fixed',
                    top: `${popupPosition.top}px`,
                    left: `${popupPosition.left}px`,
                    zIndex: 9999,
                }}
                ref={popupRef}
            >
                <div className={styles.ordinalInfoHeader}>
                    <h4>Ordinal Information</h4>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.ordinalInfoContent}>
                    {isLoading ? (
                        <div className={styles.loading}>
                            <i className="fa fa-spinner fa-spin" />
                            <span>Loading ordinal details...</span>
                        </div>
                    ) : (
                        <>
                            <div className={styles.originContainer}>
                                <div className={styles.originText} title={ordinal?.origin}>
                                    {formatOrigin(ordinal?.origin)}
                                </div>
                                <button
                                    className={styles.copyButton}
                                    onClick={handleCopyOrigin}
                                    title="Copy full origin to clipboard"
                                >
                                    {copySuccess ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                            </div>

                            <div className={styles.ordinalInfoRows}>
                                {ordinal?.inscription_id && (
                                    <div className={styles.ordinalInfoRow}>
                                        <span className={styles.label}>
                                            <Fingerprint size={14} /> Inscription
                                        </span>
                                        <span className={styles.value}>
                                            {ordinal.inscription_id.substring(0, 10)}...
                                        </span>
                                    </div>
                                )}
                                <div className={styles.ordinalInfoRow}>
                                    <span className={styles.label}>
                                        <User size={14} /> Owner
                                    </span>
                                    <span className={styles.value}>{renderOwner()}</span>
                                </div>
                                {ordinal?.inscription_number && (
                                    <div className={styles.ordinalInfoRow}>
                                        <span className={styles.label}>
                                            <Hash size={14} /> Number
                                        </span>
                                        <span className={styles.value}>
                                            {String(ordinal.inscription_number)}
                                        </span>
                                    </div>
                                )}
                                {ordinal?.content_type && (
                                    <div className={styles.ordinalInfoRow}>
                                        <span className={styles.label}>
                                            <FileType size={14} /> Type
                                        </span>
                                        <span className={styles.value}>{ordinal.content_type}</span>
                                    </div>
                                )}
                            </div>

                            <div className={styles.ordinalInfoActions}>
                                {isOwner && (
                                    <button
                                        className={styles.viewButton}
                                        onClick={() => {
                                            onClose()
                                            openOrdinalsModal(ordinal.origin)
                                        }}
                                    >
                                        <span className={styles.buttonIcon}>
                                            <Wallet size={16} />
                                        </span>
                                        View in Wallet
                                    </button>
                                )}
                            </div>

                            {explorerLinks.length > 0 && (
                                <div className={styles.explorerSection}>
                                    <div className={styles.explorerSectionTitle}>
                                        View on Explorer
                                    </div>
                                    <div className={styles.explorerButtons}>
                                        {explorerLinks.map(link => (
                                            <button
                                                key={link.key}
                                                className={cn(
                                                    styles.explorerButton,
                                                    link.className,
                                                )}
                                                onClick={() => openExternal(link.url)}
                                                title={link.title}
                                                aria-label={`View on ${link.label}`}
                                            >
                                                <span className={styles.buttonIcon}>
                                                    {link.icon}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </OrdinalInfoPopupPortal>
    )
}
